// "Previews first", animated (one 11s loop, CSS keyframes on inline SVG — see AnimatedFigure):
//   1. light previews of six photos fly from "Your computer" into "Your gallery" (washed-out tiles);
//   2. the gallery's badge flips from "Not live yet" to "Live";
//   3. a buyer taps "Buy" on one photo → "Bought";
//   4. that photo's original jumps from the back of the originals queue to the front ("Goes first");
//   5. the originals follow, the bought one first, each turning its tile from preview to original.
// Reduced motion: frozen at step 4 (gallery live, photo bought, its original at the front of the queue).
// The tiles are real running-event photos (./photos), loaded once the figure nears the viewport.
import type { CSSProperties } from "react";
import { AnimatedFigure } from "./AnimatedFigure";
import { useFigureNear } from "./figureNear";
import { runPhoto } from "./photos";
import { EASE, kf, tr, type Stop } from "./keyframes";
import type { WebUploaderCopy } from "@/content/webUploader";

const SCOPE = "wupf";
const DUR = 11;
const FREEZE = 53;

// ---- geometry (viewBox 400 × 300) -------------------------------------------------------------
const TILE = { w: 52, h: 44 };
const SMALL = 0.77; // a photo in the computer / queue, relative to a gallery tile
const SRC = (i: number) => ({ x: 22 + (i % 2) * 48, y: 50 + Math.floor(i / 2) * 44 });
const DST = (i: number) => ({ x: 208 + (i % 3) * 60, y: 76 + Math.floor(i / 3) * 56 });
const SLOT = (s: number) => ({ x: 12 + s * 48, y: 238 });

const COLORS: [string, string][] = [
  ["#ffb347", "#ff7a2f"], ["#3fb4a4", "#1d6f78"], ["#9fd3e6", "#4a86b5"],
  ["#f6d365", "#f08a24"], ["#f7a8b8", "#e0607e"], ["#9ed27a", "#3f9b6a"],
];
const N = COLORS.length;
const BOUGHT = 4;

// ---- timeline (% of the loop) -----------------------------------------------------------------
const PV_START = (i: number) => 3 + i * 3.2;
const PV_TRAVEL = 7;
const LIVE_AT = 29;
const BUY_AT = 35;
const TAP_AT = 39.5;
const BOUGHT_AT = 41.5;
const JUMP = [46, 53] as const;
const ORDER = [BOUGHT, 0, 1, 2, 3, 5];
const OG_START = (k: number) => 56 + k * 6;
const OG_TRAVEL = 5;
const END = 95; // everything fades out by 98 and the loop restarts
const STEPS = [0, LIVE_AT - 1, BUY_AT, JUMP[0], OG_START(0) - 1, END];

const name = (el: string) => `${SCOPE}-${el}`;
const anim = (el: string): CSSProperties => ({ animationName: name(el) });

const opacityWindow = (el: string, from: number, to: number, fade = 1.5): string =>
  kf(name(el), [
    [0, "opacity:0"], [from - fade, "opacity:0"], [from, "opacity:1"], [to, "opacity:1"], [to + fade, "opacity:0"], [100, "opacity:0"],
  ]);

function buildKeyframes(): string {
  const out: string[] = [];
  // 1. previews fly in and stay as washed-out tiles
  for (let i = 0; i < N; i++) {
    const s = SRC(i), d = DST(i), t = PV_START(i);
    out.push(kf(name(`pv${i}`), [
      [0, `${tr(s.x, s.y, SMALL)};opacity:0`],
      [t - 0.01, `${tr(s.x, s.y, SMALL)};opacity:0`],
      [t, `${tr(s.x, s.y, SMALL)};opacity:1;${EASE}`],
      [t + PV_TRAVEL, `${tr(d.x, d.y)};opacity:1`],
      [END, `${tr(d.x, d.y)};opacity:1`],
      [END + 3, `${tr(d.x, d.y)};opacity:0`],
      [100, `${tr(s.x, s.y, SMALL)};opacity:0`],
    ]));
  }
  // 2. the badge flips
  out.push(kf(name("notlive"), [[0, "transform:scaleY(1)"], [LIVE_AT - 1, "transform:scaleY(1)"], [LIVE_AT, "transform:scaleY(0)"], [END + 3, "transform:scaleY(0)"], [END + 4, "transform:scaleY(1)"], [100, "transform:scaleY(1)"]]));
  out.push(kf(name("live"), [[0, "transform:scaleY(0)"], [LIVE_AT, "transform:scaleY(0)"], [LIVE_AT + 1.2, "transform:scaleY(1)"], [END + 2, "transform:scaleY(1)"], [END + 3, "transform:scaleY(0)"], [100, "transform:scaleY(0)"]]));
  // 3. Buy → tap → Bought
  out.push(opacityWindow("buy", BUY_AT, BOUGHT_AT, 1));
  out.push(kf(name("tap"), [[0, "opacity:0;transform:scale(.3)"], [TAP_AT, "opacity:0;transform:scale(.3)"], [TAP_AT + 0.5, "opacity:.7;transform:scale(.5)"], [TAP_AT + 3, "opacity:0;transform:scale(1.6)"], [100, "opacity:0;transform:scale(1.6)"]]));
  out.push(opacityWindow("bought", BOUGHT_AT, END, 1));
  // 4. the queue: the bought photo's original jumps to the front, then everything goes in order, the rest
  //    moving up a place as each one leaves
  const slotAt = new Map<number, number>(); // chip → slot after the jump
  slotAt.set(BOUGHT, 0);
  [0, 1, 2, 3].forEach((c) => slotAt.set(c, c + 1));
  slotAt.set(5, 5);
  for (let c = 0; c < N; c++) {
    const stops: Stop[] = [];
    const at = (p: number, x: number, y: number, s: number, o: number, ease = false) =>
      stops.push([p, `${tr(x, y, s)};opacity:${o}${ease ? `;${EASE}` : ""}`]);
    const s0 = SLOT(c);
    at(0, s0.x, s0.y, SMALL, 0);
    at(2, s0.x, s0.y, SMALL, 1);
    // the jump
    let slot = slotAt.get(c)!;
    if (c === BOUGHT) {
      at(JUMP[0], s0.x, s0.y, SMALL, 1, true);
      at(JUMP[0] + 2, s0.x, s0.y - 30, SMALL, 1, true);
      at(JUMP[1] - 2, SLOT(0).x, s0.y - 30, SMALL, 1, true);
      at(JUMP[1], SLOT(0).x, SLOT(0).y, SMALL, 1);
    } else if (slot !== c) {
      at(JUMP[0] + 1, s0.x, s0.y, SMALL, 1, true);
      at(JUMP[0] + 4, SLOT(slot).x, SLOT(slot).y, SMALL, 1);
    }
    // departures before this chip's turn shuffle it forward
    const turn = ORDER.indexOf(c);
    for (let k = 0; k < turn; k++) {
      const t = OG_START(k);
      at(t + 0.5, SLOT(slot).x, SLOT(slot).y, SMALL, 1, true);
      slot -= 1;
      at(t + 2, SLOT(slot).x, SLOT(slot).y, SMALL, 1);
    }
    // its own trip up to the gallery
    const t = OG_START(turn), d = DST(c), p = SLOT(slot);
    at(t + 2.2, p.x, p.y, SMALL, 1, true);
    at(t + 2.2 + OG_TRAVEL, d.x, d.y, 1, 1);
    at(t + 3.2 + OG_TRAVEL, d.x, d.y, 1, 0);
    at(100, d.x, d.y, 1, 0);
    out.push(kf(name(`og${c}`), stops));
    // the tile turns from preview to original as it lands
    out.push(opacityWindow(`done${c}`, t + 2.2 + OG_TRAVEL, END, 0.8));
  }
  out.push(opacityWindow("ring", BOUGHT_AT, OG_START(0) + 2.2, 1));
  out.push(opacityWindow("first", JUMP[1], OG_START(0) + 2, 1));
  // the step list, in step with the scene
  for (let i = 0; i < STEPS.length - 1; i++) {
    const a = STEPS[i], b = STEPS[i + 1];
    out.push(kf(name(`step${i}`), [
      [0, i === 0 ? "opacity:1" : "opacity:.45"], [Math.max(0, a - 0.5), i === 0 ? "opacity:1" : "opacity:.45"], [a + 0.5, "opacity:1"],
      [b - 0.5, "opacity:1"], [b + 0.5, "opacity:.45"], [99, i === 0 ? "opacity:.45" : "opacity:.45"], [100, i === 0 ? "opacity:1" : "opacity:.45"],
    ]));
  }
  return out.join("");
}

const KEYFRAMES = buildKeyframes();

/** A photo tile at the origin (positioned by its animation): the photo over a placeholder gradient, washed
 *  out for a preview. */
const Photo = ({ i, preview }: { i: number; preview?: boolean }) => {
  const near = useFigureNear();
  return (
    <>
      <rect width={TILE.w} height={TILE.h} rx={5} fill={`url(#${SCOPE}-g${i})`} />
      {near && (
        <image href={runPhoto(i)} width={TILE.w} height={TILE.h} preserveAspectRatio="xMidYMid slice" clipPath={`url(#${SCOPE}-clip)`} />
      )}
      {preview && <rect width={TILE.w} height={TILE.h} rx={5} fill="#fff" opacity={0.5} />}
    </>
  );
};

export const PreviewsFirstAnimation = ({ copy, pause, play }: { copy: WebUploaderCopy["previews"]; pause: string; play: string }) => {
  const a = copy.anim;
  const pill = { x: 208, y: 40, w: 128, h: 22 };
  const buyTile = DST(BOUGHT);
  return (
    <AnimatedFigure
      scope={SCOPE}
      durationS={DUR}
      freezeAt={FREEZE}
      keyframes={KEYFRAMES}
      label={a.label}
      pauseLabel={pause}
      playLabel={play}
      description={
        <>
          <ol className="grid gap-1.5 text-sm text-foreground">
            {copy.steps.map((s, i) => (
              <li key={s.id} data-a="" style={anim(`step${i}`)} className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-hawk-orange text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                {s.text}
              </li>
            ))}
          </ol>
          <p className="mt-3">{a.description}</p>
        </>
      }
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-lg sm:p-4">
        <svg viewBox="0 0 400 300" className="block h-auto w-full" role="presentation" focusable="false">
          <defs>
            <clipPath id={`${SCOPE}-clip`}>
              <rect width={TILE.w} height={TILE.h} rx={5} />
            </clipPath>
            {COLORS.map(([c1, c2], i) => (
              <linearGradient key={i} id={`${SCOPE}-g${i}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={c1} />
                <stop offset="1" stopColor={c2} />
              </linearGradient>
            ))}
          </defs>

          {/* Your computer */}
          <text x={66} y={26} textAnchor="middle" className="fill-muted-foreground" fontSize={12} fontWeight={600}>{a.computer}</text>
          <rect x={10} y={36} width={112} height={150} rx={10} className="fill-secondary stroke-border" />
          {Array.from({ length: N }, (_, i) => (
            <g key={i} transform={`translate(${SRC(i).x} ${SRC(i).y}) scale(${SMALL})`}>
              <Photo i={i} />
            </g>
          ))}

          {/* Your gallery */}
          <rect x={196} y={8} width={194} height={194} rx={10} className="fill-background stroke-border" />
          <text x={208} y={30} className="fill-foreground" fontSize={13} fontWeight={700}>{a.gallery}</text>
          <g data-a="" data-flip="" style={anim("notlive")}>
            <rect x={pill.x} y={pill.y} width={pill.w} height={pill.h} rx={11} className="fill-muted" />
            <text x={pill.x + pill.w / 2} y={pill.y + 15} textAnchor="middle" className="fill-muted-foreground" fontSize={11} fontWeight={600}>{a.notLive}</text>
          </g>
          <g data-a="" data-flip="" style={anim("live")}>
            <rect x={pill.x} y={pill.y} width={pill.w} height={pill.h} rx={11} fill="#059669" />
            <circle cx={pill.x + 14} cy={pill.y + 11} r={3.5} fill="#fff" />
            <text x={pill.x + pill.w / 2 + 6} y={pill.y + 15} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={700}>{a.live}</text>
          </g>
          {Array.from({ length: N }, (_, i) => (
            <rect key={i} x={DST(i).x} y={DST(i).y} width={TILE.w} height={TILE.h} rx={5} fill="none" className="stroke-border" strokeDasharray="3 3" />
          ))}
          {Array.from({ length: N }, (_, i) => (
            <g key={i} data-a="" style={anim(`pv${i}`)}>
              <Photo i={i} preview />
            </g>
          ))}
          {/* originals landed: full colour + a tick */}
          {Array.from({ length: N }, (_, i) => (
            <g key={i} data-a="" style={anim(`done${i}`)} transform={`translate(${DST(i).x} ${DST(i).y})`}>
              <Photo i={i} />
              <circle cx={TILE.w - 8} cy={8} r={6} fill="#059669" />
              <path d={`M${TILE.w - 11} 8 l2 2 l4 -4`} stroke="#fff" strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          ))}
          {/* Buy → Bought on one photo */}
          <g data-a="" style={anim("buy")}>
            <rect x={buyTile.x + 1} y={buyTile.y + 27} width={50} height={14} rx={7} className="fill-hawk-orange" />
            <text x={buyTile.x + 26} y={buyTile.y + 37.5} textAnchor="middle" fill="#fff" fontSize={8.5} fontWeight={700}>{a.buy}</text>
          </g>
          <circle data-a="" data-flip="" style={anim("tap")} cx={buyTile.x + 26} cy={buyTile.y + 34} r={12} fill="none" stroke="#ff7a2f" strokeWidth={2} />
          <g data-a="" style={anim("bought")}>
            <rect x={buyTile.x - 2} y={buyTile.y - 2} width={TILE.w + 4} height={TILE.h + 4} rx={7} fill="none" stroke="#ff7a2f" strokeWidth={2.5} />
            <rect x={buyTile.x - 3} y={buyTile.y + 27} width={58} height={14} rx={7} fill="#1f2937" />
            <text x={buyTile.x + 26} y={buyTile.y + 37.5} textAnchor="middle" fill="#fff" fontSize={8.5} fontWeight={700}>{a.bought}</text>
          </g>

          {/* Originals queue */}
          <text x={10} y={228} className="fill-muted-foreground" fontSize={12} fontWeight={600}>{a.queue}</text>
          <line x1={10} y1={286} x2={300} y2={286} className="stroke-border" strokeWidth={1} />
          <g data-a="" style={anim("first")}>
            <text x={SLOT(0).x + 20} y={298} textAnchor="middle" className="fill-hawk-orange" fontSize={11} fontWeight={700}>{a.goesFirst}</text>
          </g>
          {Array.from({ length: N }, (_, i) => (
            <g key={i} data-a="" style={anim(`og${i}`)}>
              <Photo i={i} />
              {i === BOUGHT && (
                <rect data-a="" style={anim("ring")} x={-3} y={-3} width={TILE.w + 6} height={TILE.h + 6} rx={8} fill="none" stroke="#ff7a2f" strokeWidth={3} />
              )}
            </g>
          ))}
        </svg>
      </div>
    </AnimatedFigure>
  );
};
